import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ReportsDashboard from "@/components/ReportsDashboard";
import RefundsDashboard from "@/components/RefundsDashboard";
import { 
  Building2, 
  Users, 
  DollarSign,
  Calendar,
  Megaphone,
  QrCode,
  Plus,
  Check,
  X,
  Eye,
  Send,
  Download,
  Share2,
  Copy,
  Gift,
  Percent,
  Clock,
  Mail,
  Phone,
  Instagram,
  Briefcase,
  MapPin,
  FileText,
  Sparkles,
  ChevronRight,
  Home,
  Settings,
  LogOut,
  Crown,
  Edit3,
  Trash2,
  Link,
  Image,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Loader2,
  BarChart3
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import BoothManagement from "@/components/BoothManagement";
import BoothMarketingManager from "@/components/BoothMarketingManager";
import RenterApplicationsManager from "@/components/RenterApplicationsManager";
import RenterHistoryManager from "@/components/RenterHistoryManager";
import QRCode from 'qrcode';
import { ArrowRight, ArrowLeft, Lock, CheckCircle2, HelpCircle, CreditCard, Shield, BookOpen } from "lucide-react";
import TrialExpiredModal from "@/components/TrialExpiredModal";
import SoldierRecruitmentPlaybook from "@/components/SoldierRecruitmentPlaybook";

const SUPER_ADMIN_EMAIL = "georgebroughton71@gmail.com";

const OwnerDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [membershipTier, setMembershipTier] = useState("free");
  const [stripeConnected, setStripeConnected] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(false);
  
  // Applications state
  const [applications, setApplications] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveForm, setApproveForm] = useState({
    booth_number: "",
    rent_amount: "",
    payment_due_day: 1,
    notes: ""
  });
  
  // Promo codes state
  const [promoCodes, setPromoCodes] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: "",
    promo_type: "free_months",
    value: 1,
    description: "",
    max_uses: 0,
    valid_until: ""
  });
  
  // Marketing state
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [applicationUrl, setApplicationUrl] = useState("");
  const canvasRef = useRef(null);
  
  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState("");
  
  // Settings state
  const [boothSettings, setBoothSettings] = useState({
    auto_reminders_enabled: true,
    reminder_days_before: [7, 3, 1],
    reminder_email_enabled: true,
    reminder_sms_enabled: false,
    thank_you_message_enabled: true,
    thank_you_message_template: "Thank you {renter_name} for your payment of {amount}! Your next rent of {next_amount} is due on {next_due_date}. We appreciate you being part of our salon family!",
    stripe_auto_payment_enabled: false,
    stripe_recurring_enabled: false,
    // Payment method options
    accept_stripe_payments: true,
    accept_cashapp: false,
    cashapp_tag: "",
    accept_zelle: false,
    zelle_email: "",
    zelle_phone: "",
    accept_paypal: false,
    paypal_email: "",
    accept_cash: false,  // Cash payment at salon
    payment_export_format: "csv",
    // Monthly auto payment link settings
    auto_monthly_links_enabled: false,
    billing_day: 1
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  const [generatingMonthlyLinks, setGeneratingMonthlyLinks] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    totalRenters: 0,
    monthlyRevenue: 0,
    pendingApps: 0,
    activePromos: 0
  });
  
  // Trial expiration state
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const [trialChecked, setTrialChecked] = useState(false);
  
  // Check if user is Super Admin or Elite
  const isSuperAdmin = user?.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase();
  const isElite = isSuperAdmin || membershipTier === "elite";
  const isSoldier = isSuperAdmin || (user?.is_soldier && user?.soldier_status === 'active');
  
  // Show upgrade prompt for non-elite users
  const showUpgradePrompt = (feature) => {
    if (!isElite) {
      setUpgradeFeature(feature);
      setShowUpgradeModal(true);
      return true;
    }
    return false;
  };

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      const userData = JSON.parse(storedUser);
      setUser(userData);
      // Set membership tier from stored user
      setMembershipTier(userData.membership_tier || userData.membershipTier || "free");
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (token && user) {
      fetchData();
      generateQRCode();
      fetchMembershipStatus();
      checkTrialStatus();
    }
  }, [token, user]);
  
  // Check trial status on mount
  const checkTrialStatus = async () => {
    if (!token || trialChecked) return;
    
    try {
      const res = await axios.get(`${API}/membership/trial/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If user was on trial and it expired, show modal
      if (res.data.trial_expired || res.data.trial_exhausted) {
        setShowTrialExpiredModal(true);
      }
      setTrialChecked(true);
    } catch (error) {
      console.log("Trial status check:", error.response?.status);
      setTrialChecked(true);
    }
  };

  // Handle trial expired modal close - downgrade to free
  const handleTrialExpiredContinueFree = async () => {
    try {
      // Update local user state to reflect free tier
      if (user) {
        const updatedUser = {
          ...user,
          membership: { ...user.membership, tier: 'free', is_trial: false }
        };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setMembershipTier('free');
      }
      setShowTrialExpiredModal(false);
      toast.info("You're now on the free plan. Upgrade anytime to access premium features.");
    } catch (error) {
      console.error("Error handling trial expiration:", error);
    }
  };
  
  const fetchMembershipStatus = async () => {
    try {
      // Fetch latest membership status and Stripe connection status
      const [membershipRes, profileRes] = await Promise.all([
        axios.get(`${API}/membership/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { tier: "free" } })),
        axios.get(`${API}/stylists/my-profile`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} }))
      ]);
      
      setMembershipTier(membershipRes.data?.tier || "free");
      setStripeConnected(profileRes.data?.stripe_connected || false);
    } catch (error) {
      console.error("Error fetching membership:", error);
    }
  };

  const fetchData = async () => {
    try {
      const salonId = user?.salon_id || user?.user_id;
      const [appsRes, promosRes, rentersRes, settingsRes, boothAppsRes] = await Promise.all([
        axios.get(`${API}/booth/applications`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/booth/promo-codes`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/booth/renters`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/booth/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} })),
        // Also fetch from new booth management system
        axios.get(`${API}/booths/salon/${salonId}/applications`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { applications: [] } }))
      ]);
      
      // Combine applications from both old and new booth management systems
      const oldApplications = appsRes.data || [];
      const newApplications = boothAppsRes.data?.applications || [];
      const allApplications = [...oldApplications, ...newApplications];
      setApplications(allApplications);
      setPromoCodes(promosRes.data || []);
      
      // Load settings
      if (settingsRes.data && Object.keys(settingsRes.data).length > 0) {
        setBoothSettings(prev => ({...prev, ...settingsRes.data}));
      }
      
      // Combine pending counts from both systems
      const oldPending = oldApplications.filter(a => a.status === "pending").length;
      const newPending = newApplications.filter(a => a.status === "pending").length;
      const totalPending = oldPending + newPending;
      setPendingCount(totalPending);
      
      const renters = rentersRes.data || [];
      const monthlyRev = renters.reduce((sum, r) => {
        if (r.is_free_rent) return sum;
        let amt = r.rent_amount || 0;
        if (r.discount_percent > 0) amt *= (1 - r.discount_percent / 100);
        if (r.discount_amount > 0) amt = Math.max(0, amt - r.discount_amount);
        return sum + amt;
      }, 0);
      
      setStats({
        totalRenters: renters.length,
        monthlyRevenue: monthlyRev,
        pendingApps: totalPending,
        activePromos: (promosRes.data || []).filter(p => p.is_active).length
      });
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async () => {
    if (!user?.user_id) return;
    
    const appUrl = `${window.location.origin}/apply-booth/${user.user_id}`;
    setApplicationUrl(appUrl);
    
    try {
      const qr = await QRCode.toDataURL(appUrl, {
        width: 200,
        margin: 2,
        color: { dark: '#000000', light: '#ffffff' }
      });
      setQrCodeUrl(qr);
    } catch (error) {
      console.error("QR generation error:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const approveApplication = async () => {
    if (!selectedApp) return;
    
    try {
      await axios.put(
        `${API}/booth/applications/${selectedApp.application_id}/approve?booth_number=${approveForm.booth_number}&rent_amount=${approveForm.rent_amount}&payment_due_day=${approveForm.payment_due_day}&notes=${encodeURIComponent(approveForm.notes)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Application approved! Renter added.");
      setShowApproveModal(false);
      setSelectedApp(null);
      setApproveForm({ booth_number: "", rent_amount: "", payment_due_day: 1, notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    }
  };

  const rejectApplication = async (appId, reason = "") => {
    try {
      await axios.put(
        `${API}/booth/applications/${appId}/reject?reason=${encodeURIComponent(reason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Application rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject application");
    }
  };

  const createPromoCode = async () => {
    try {
      await axios.post(`${API}/booth/promo-codes`, {
        ...newPromo,
        owner_id: user.user_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Promo code created!");
      setShowPromoModal(false);
      setNewPromo({
        code: "",
        promo_type: "free_months",
        value: 1,
        description: "",
        max_uses: 0,
        valid_until: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create promo");
    }
  };

  const togglePromoActive = async (promoId, isActive) => {
    try {
      await axios.put(
        `${API}/booth/promo-codes/${promoId}?is_active=${!isActive}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      toast.error("Failed to update promo");
    }
  };

  const deletePromo = async (promoId) => {
    if (!confirm("Delete this promo code?")) return;
    try {
      await axios.delete(`${API}/booth/promo-codes/${promoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Promo deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete promo");
    }
  };

  const saveBoothSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/booth/settings`, boothSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const triggerRentReminders = async () => {
    setTriggeringReminders(true);
    try {
      const response = await axios.post(`${API}/booth/trigger-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.reminders_sent > 0) {
        toast.success(`Sent ${response.data.reminders_sent} rent reminders!`);
      } else {
        toast.info(response.data.message || "No reminders to send today");
      }
    } catch (error) {
      toast.error("Failed to trigger reminders");
    } finally {
      setTriggeringReminders(false);
    }
  };

  const exportPaymentHistory = async (format = "csv") => {
    try {
      const response = await axios.get(`${API}/booth/export-payments?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: format === "csv" ? "blob" : "json"
      });
      
      if (format === "csv") {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `rent_payments_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success("Payment history exported!");
      } else {
        // JSON - download as file
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `rent_payments_${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success("Payment history exported!");
      }
    } catch (error) {
      toast.error("Failed to export payment history");
    }
  };

  const toggleReminderDay = (day) => {
    setBoothSettings(prev => {
      const days = prev.reminder_days_before || [];
      if (days.includes(day)) {
        return {...prev, reminder_days_before: days.filter(d => d !== day)};
      } else {
        return {...prev, reminder_days_before: [...days, day].sort((a, b) => b - a)};
      }
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const downloadMarketingCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = 1080;
    canvas.height = 1080;
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#1a1a2e');
    gradient.addColorStop(1, '#16213e');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Decorative circles
    ctx.fillStyle = 'rgba(139, 92, 246, 0.1)';
    ctx.beginPath();
    ctx.arc(900, 100, 200, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(100, 900, 150, 0, Math.PI * 2);
    ctx.fill();
    
    // Title
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 64px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('BOOTH SPACE', canvas.width / 2, 150);
    ctx.fillText('AVAILABLE', canvas.width / 2, 220);
    
    // Subtitle
    ctx.fillStyle = '#a78bfa';
    ctx.font = '32px Arial';
    ctx.fillText('Join Our Salon Family', canvas.width / 2, 290);
    
    // Features
    ctx.fillStyle = '#e2e8f0';
    ctx.font = '28px Arial';
    ctx.textAlign = 'left';
    const features = [
      '✓ Prime Location',
      '✓ Flexible Terms',
      '✓ Professional Environment',
      '✓ Growth Opportunity'
    ];
    features.forEach((f, i) => {
      ctx.fillText(f, 100, 400 + i * 50);
    });
    
    // QR Code placeholder area
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(700, 380, 280, 280);
    
    // Draw QR code if available
    if (qrCodeUrl) {
      const qrImg = new window.Image();
      qrImg.crossOrigin = 'anonymous';
      qrImg.onload = () => {
        ctx.drawImage(qrImg, 710, 390, 260, 260);
        
        // CTA
        ctx.fillStyle = '#a78bfa';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('SCAN TO APPLY', 840, 700);
        
        // Bottom branding
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 36px Arial';
        ctx.fillText('MyHairChair', canvas.width / 2, 950);
        ctx.font = '20px Arial';
        ctx.fillStyle = '#94a3b8';
        ctx.fillText('Professional Booth Rentals', canvas.width / 2, 990);
        
        // Download
        const link = document.createElement('a');
        link.download = 'booth-rental-card.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      };
      qrImg.src = qrCodeUrl;
    }
  };

  const shareToSocial = (platform) => {
    const text = `🪑 Booth space available! Join our salon family. Professional environment, flexible terms. Apply now: ${applicationUrl}`;
    const encodedText = encodeURIComponent(text);
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(applicationUrl)}&quote=${encodedText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(applicationUrl)}`
    };
    
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hidden canvas for marketing card generation */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">Salon Owner Dashboard</h1>
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-500">{user?.name || 'Salon Owner'}</p>
                {isElite ? (
                  <Badge className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs">
                    <Crown className="w-3 h-3 mr-1" />
                    Elite
                  </Badge>
                ) : membershipTier === "pro" ? (
                  <Badge className="bg-purple-500 text-white text-xs">Pro</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs">Free</Badge>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {isSoldier && (
              <Button
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                onClick={() => setShowPlaybook(true)}
                data-testid="owner-playbook-btn"
              >
                <BookOpen className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Recruitment Playbook</span>
              </Button>
            )}
            {!isElite && (
              <Button
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700"
                onClick={() => navigate('/membership?feature=salon_owner&tier=elite')}
              >
                <Crown className="w-4 h-4 mr-1" />
                Upgrade to Elite
              </Button>
            )}
            {pendingCount > 0 && (
              <Badge className="bg-red-500 text-white">
                {pendingCount} Pending
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/stylist')}
              data-testid="stylist-view-btn"
            >
              <Home className="w-4 h-4 mr-1" />
              Stylist View
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              data-testid="home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Home
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/user-guide#owner-guide')}
              data-testid="owner-guide-btn"
            >
              <HelpCircle className="w-4 h-4 mr-1" />
              Guide
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <Users className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{stats.totalRenters}</p>
              <p className="text-sm opacity-80">Active Renters</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <CardContent className="p-4">
              <DollarSign className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-3xl font-bold">${stats.monthlyRevenue.toLocaleString()}</p>
              <p className="text-sm opacity-80">Monthly Revenue</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <FileText className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{stats.pendingApps}</p>
              <p className="text-sm opacity-80">Pending Apps</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-pink-500 to-rose-600 text-white">
            <CardContent className="p-4">
              <Gift className="w-8 h-8 mb-2 opacity-80" />
              <p className="text-3xl font-bold">{stats.activePromos}</p>
              <p className="text-sm opacity-80">Active Promos</p>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 rounded-xl shadow-sm">
            <TabsTrigger value="overview" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <Home className="w-4 h-4 mr-1" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="applications" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white relative">
              <FileText className="w-4 h-4 mr-1" />
              Applications
              {pendingCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="marketing" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <Megaphone className="w-4 h-4 mr-1" />
              Marketing
            </TabsTrigger>
            <TabsTrigger value="promos" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <Gift className="w-4 h-4 mr-1" />
              Promo Codes
            </TabsTrigger>
            <TabsTrigger value="renters" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-1" />
              Renters
            </TabsTrigger>
            <TabsTrigger value="reports" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <BarChart3 className="w-4 h-4 mr-1" />
              Reports
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-1" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Quick Actions */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    Quick Actions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full justify-start bg-gradient-to-r from-purple-500 to-indigo-600"
                    onClick={() => setActiveTab("marketing")}
                  >
                    <Megaphone className="w-5 h-5 mr-2" />
                    Create Marketing Card
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => setActiveTab("promos")}
                  >
                    <Gift className="w-5 h-5 mr-2" />
                    Create Promo Code
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => copyToClipboard(applicationUrl)}
                  >
                    <Link className="w-5 h-5 mr-2" />
                    Copy Application Link
                  </Button>
                </CardContent>
              </Card>

              {/* Application Link QR */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-purple-500" />
                    Your Application QR Code
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  {qrCodeUrl && (
                    <img src={qrCodeUrl} alt="Application QR" className="mx-auto mb-4 rounded-lg shadow" />
                  )}
                  <p className="text-sm text-gray-500 mb-3">
                    Stylists scan this to apply for booth rental
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(applicationUrl)}>
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      const link = document.createElement('a');
                      link.download = 'booth-application-qr.png';
                      link.href = qrCodeUrl;
                      link.click();
                    }}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Applications */}
            <Card className="bg-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Recent Applications</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => setActiveTab("applications")}>
                  View All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {applications.slice(0, 3).map((app) => (
                  <div key={app.application_id} className="flex items-center justify-between py-3 border-b last:border-0">
                    <div>
                      <p className="font-medium">{app.applicant_name || app.full_name || 'Unknown'}</p>
                      <p className="text-sm text-gray-500">{app.specialty || app.professional_type || 'Not specified'} • {app.years_experience || 0} years exp</p>
                    </div>
                    <Badge className={
                      app.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                      app.status === 'approved' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }>
                      {app.status}
                    </Badge>
                  </div>
                ))}
                {applications.length === 0 && (
                  <p className="text-center text-gray-500 py-8">No applications yet. Share your QR code to get started!</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">Booth Applications</h2>
              <div className="flex gap-2">
                <Badge variant="outline">Total: {applications.length}</Badge>
                <Badge className="bg-blue-100 text-blue-700">Pending: {pendingCount}</Badge>
              </div>
            </div>

            {applications.map((app) => (
              <Card key={app.application_id} className={app.status === 'pending' ? 'border-blue-300' : ''}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-lg">{app.applicant_name || app.full_name || 'Unknown'}</h3>
                        <Badge className={
                          app.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                          app.status === 'approved' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }>
                          {app.status}
                        </Badge>
                        {app.promo_code && (
                          <Badge className="bg-purple-100 text-purple-700">
                            <Gift className="w-3 h-3 mr-1" />
                            {app.promo_code}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          {app.specialty || app.professional_type || 'Not specified'}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {app.years_experience || 0} years exp
                        </div>
                        <div className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {app.applicant_email || app.email || 'N/A'}
                        </div>
                        {(app.applicant_phone || app.phone) && (
                          <div className="flex items-center gap-1">
                            <Phone className="w-4 h-4" />
                            {app.applicant_phone || app.phone}
                          </div>
                        )}
                      </div>

                      {app.why_interested && (
                        <p className="text-sm text-gray-600 italic">&quot;{app.why_interested}&quot;</p>
                      )}
                      
                      <p className="text-xs text-gray-400">
                        Applied: {new Date(app.created_at || app.submitted_at).toLocaleDateString()}
                      </p>
                    </div>

                    {app.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => {
                            setSelectedApp(app);
                            setShowApproveModal(true);
                          }}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 border-red-300 hover:bg-red-50"
                          onClick={() => rejectApplication(app.application_id)}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {applications.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No applications yet</p>
                  <p className="text-sm text-gray-400">Share your application link or QR code to receive applications</p>
                  <Button className="mt-4" onClick={() => setActiveTab("marketing")}>
                    <Megaphone className="w-4 h-4 mr-1" />
                    Create Marketing Card
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Marketing Tab */}
          <TabsContent value="marketing" className="space-y-6">
            <BoothMarketingManager 
              salonId={user?.salon_id || user?.user_id}
              salonInfo={{
                name: user?.business_name || user?.name,
                phone: user?.phone,
                email: user?.email,
                address: user?.address,
                city: user?.city,
                state: user?.state,
                zip_code: user?.zip_code
              }}
            />
            
            {/* Legacy Marketing Tools */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Share Options */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Share2 className="w-5 h-5 text-purple-500" />
                    Quick Share Links
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm text-gray-500">Application Link</Label>
                    <div className="flex gap-2 mt-1">
                      <Input value={applicationUrl} readOnly className="text-sm" />
                      <Button size="icon" variant="outline" onClick={() => copyToClipboard(applicationUrl)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <Button variant="outline" onClick={() => shareToSocial('twitter')}>
                      <span className="text-blue-400">𝕏</span>
                      <span className="ml-1">Twitter</span>
                    </Button>
                    <Button variant="outline" onClick={() => shareToSocial('facebook')}>
                      <span className="text-blue-600">f</span>
                      <span className="ml-1">Facebook</span>
                    </Button>
                    <Button variant="outline" onClick={() => shareToSocial('linkedin')}>
                      <span className="text-blue-700">in</span>
                      <span className="ml-1">LinkedIn</span>
                    </Button>
                  </div>

                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      window.location.href = `mailto:?subject=Booth Space Available&body=Looking for a booth rental? Apply here: ${applicationUrl}`;
                    }}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Share via Email
                  </Button>

                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      window.location.href = `sms:?body=Looking for a booth rental? Apply here: ${applicationUrl}`;
                    }}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Share via SMS
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promos" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">Promo Codes</h2>
              <Dialog open={showPromoModal} onOpenChange={setShowPromoModal}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-purple-500 to-indigo-600">
                    <Plus className="w-4 h-4 mr-1" />
                    Create Promo Code
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Promo Code</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Promo Code</Label>
                      <Input
                        value={newPromo.code}
                        onChange={(e) => setNewPromo({...newPromo, code: e.target.value.toUpperCase()})}
                        placeholder="FREEMONTH"
                      />
                    </div>
                    <div>
                      <Label>Promo Type</Label>
                      <Select
                        value={newPromo.promo_type}
                        onValueChange={(v) => setNewPromo({...newPromo, promo_type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free_months">Free Months</SelectItem>
                          <SelectItem value="percent_off">Percentage Off</SelectItem>
                          <SelectItem value="fixed_off">Fixed Amount Off</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>
                        {newPromo.promo_type === 'free_months' ? 'Number of Free Months' :
                         newPromo.promo_type === 'percent_off' ? 'Discount Percentage' :
                         'Discount Amount ($)'}
                      </Label>
                      <Input
                        type="number"
                        value={newPromo.value}
                        onChange={(e) => setNewPromo({...newPromo, value: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                    <div>
                      <Label>Description (shown to applicants)</Label>
                      <Input
                        value={newPromo.description}
                        onChange={(e) => setNewPromo({...newPromo, description: e.target.value})}
                        placeholder="First month free!"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Max Uses (0 = unlimited)</Label>
                        <Input
                          type="number"
                          value={newPromo.max_uses}
                          onChange={(e) => setNewPromo({...newPromo, max_uses: parseInt(e.target.value) || 0})}
                        />
                      </div>
                      <div>
                        <Label>Valid Until (optional)</Label>
                        <Input
                          type="date"
                          value={newPromo.valid_until}
                          onChange={(e) => setNewPromo({...newPromo, valid_until: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowPromoModal(false)}>Cancel</Button>
                    <Button onClick={createPromoCode}>Create Promo</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {promoCodes.map((promo) => (
                <Card key={promo.promo_id} className={!promo.is_active ? 'opacity-60' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <code className="bg-purple-100 text-purple-700 px-2 py-1 rounded font-bold">
                            {promo.code}
                          </code>
                          {!promo.is_active && (
                            <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          {promo.promo_type === 'free_months' && `${promo.value} month(s) free`}
                          {promo.promo_type === 'percent_off' && `${promo.value}% off`}
                          {promo.promo_type === 'fixed_off' && `$${promo.value} off`}
                        </p>
                        {promo.description && (
                          <p className="text-xs text-gray-500 italic">{promo.description}</p>
                        )}
                        <div className="flex gap-4 mt-2 text-xs text-gray-400">
                          <span>Used: {promo.current_uses}/{promo.max_uses || '∞'}</span>
                          {promo.valid_until && <span>Expires: {new Date(promo.valid_until).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => togglePromoActive(promo.promo_id, promo.is_active)}
                        >
                          {promo.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-red-500"
                          onClick={() => deletePromo(promo.promo_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {promoCodes.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Gift className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No promo codes yet</p>
                  <p className="text-sm text-gray-400">Create promo codes to attract new booth renters</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Renters Tab */}
          <TabsContent value="renters" className="space-y-6">
            <Tabs defaultValue="applications" className="space-y-4">
              <TabsList className="bg-purple-500/10 border border-purple-500/30">
                <TabsTrigger value="applications" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                  📋 Applications
                </TabsTrigger>
                <TabsTrigger value="current" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                  👥 Current Renters
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                  📜 History
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="applications">
                <RenterApplicationsManager salonId={user?.salon_id || user?.user_id} />
              </TabsContent>
              
              <TabsContent value="current">
                <BoothManagement token={token} />
              </TabsContent>
              
              <TabsContent value="history">
                <RenterHistoryManager salonId={user?.salon_id || user?.user_id} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">
            <Tabs defaultValue="rent" className="space-y-4">
              <TabsList className="bg-purple-500/10 border border-purple-500/30">
                <TabsTrigger value="rent" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                  💰 Rent Collection
                </TabsTrigger>
                <TabsTrigger value="renters" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                  👥 Renter Performance
                </TabsTrigger>
                <TabsTrigger value="refunds" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white">
                  💸 Refunds
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="rent">
                <ReportsDashboard 
                  token={token} 
                  reportType="owner" 
                  title="Rent Collection Report"
                />
              </TabsContent>
              
              <TabsContent value="renters">
                <ReportsDashboard 
                  token={token} 
                  reportType="owner_renters" 
                  title="Renter Performance Report"
                />
              </TabsContent>

              <TabsContent value="refunds">
                <RefundsDashboard />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Automation Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-500" />
                    Rent Reminder Automation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Enable Auto Reminders</Label>
                      <p className="text-sm text-gray-500">Automatically remind renters before rent is due</p>
                    </div>
                    <Switch
                      checked={boothSettings.auto_reminders_enabled}
                      onCheckedChange={(v) => setBoothSettings(prev => ({...prev, auto_reminders_enabled: v}))}
                    />
                  </div>

                  {boothSettings.auto_reminders_enabled && (
                    <>
                      <div>
                        <Label className="text-sm text-gray-600 mb-2 block">Reminder Days Before Due</Label>
                        <div className="flex flex-wrap gap-2">
                          {[14, 7, 5, 3, 2, 1].map(day => (
                            <Button
                              key={day}
                              size="sm"
                              variant={boothSettings.reminder_days_before?.includes(day) ? "default" : "outline"}
                              onClick={() => toggleReminderDay(day)}
                              className={boothSettings.reminder_days_before?.includes(day) ? "bg-purple-500" : ""}
                            >
                              {day} day{day > 1 ? 's' : ''}
                            </Button>
                          ))}
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          Selected: {boothSettings.reminder_days_before?.length > 0 
                            ? boothSettings.reminder_days_before.sort((a,b) => b-a).join(', ') + ' days before due'
                            : 'None'}
                        </p>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t">
                        <div>
                          <Label>Email Reminders</Label>
                          <p className="text-xs text-gray-500">Send reminder emails</p>
                        </div>
                        <Switch
                          checked={boothSettings.reminder_email_enabled}
                          onCheckedChange={(v) => setBoothSettings(prev => ({...prev, reminder_email_enabled: v}))}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>SMS Reminders</Label>
                          <p className="text-xs text-gray-500">⚠️ SMS requires A2P 10DLC registration</p>
                        </div>
                        <Switch
                          checked={boothSettings.reminder_sms_enabled}
                          onCheckedChange={(v) => setBoothSettings(prev => ({...prev, reminder_sms_enabled: v}))}
                          disabled
                        />
                      </div>
                    </>
                  )}

                  <Button
                    className="w-full"
                    variant="outline"
                    onClick={triggerRentReminders}
                    disabled={triggeringReminders}
                  >
                    {triggeringReminders ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Reminders Now
                  </Button>
                </CardContent>
              </Card>

              {/* Thank You Message Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-500" />
                    Thank You Message
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Send Thank You After Payment</Label>
                      <p className="text-sm text-gray-500">Auto-send when rent is marked paid</p>
                    </div>
                    <Switch
                      checked={boothSettings.thank_you_message_enabled}
                      onCheckedChange={(v) => setBoothSettings(prev => ({...prev, thank_you_message_enabled: v}))}
                    />
                  </div>

                  {boothSettings.thank_you_message_enabled && (
                    <div>
                      <Label className="text-sm text-gray-600 mb-2 block">Message Template</Label>
                      <Textarea
                        value={boothSettings.thank_you_message_template}
                        onChange={(e) => setBoothSettings(prev => ({...prev, thank_you_message_template: e.target.value}))}
                        rows={4}
                        className="text-sm"
                      />
                      <p className="text-xs text-gray-400 mt-2">
                        Available variables: {'{renter_name}'}, {'{amount}'}, {'{next_amount}'}, {'{next_due_date}'}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Stripe Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-purple-500" />
                    Payment Automation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-base">Stripe Auto-Mark Paid</Label>
                      <p className="text-sm text-gray-500">Auto-mark payments when Stripe confirms</p>
                    </div>
                    <Switch
                      checked={boothSettings.stripe_auto_payment_enabled}
                      onCheckedChange={(v) => setBoothSettings(prev => ({...prev, stripe_auto_payment_enabled: v}))}
                    />
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <div>
                      <Label className="text-base">Recurring Payments</Label>
                      <p className="text-sm text-gray-500">Enable Stripe recurring billing</p>
                      <Badge variant="outline" className="mt-1 text-xs">Coming Soon</Badge>
                    </div>
                    <Switch
                      checked={boothSettings.stripe_recurring_enabled}
                      onCheckedChange={(v) => setBoothSettings(prev => ({...prev, stripe_recurring_enabled: v}))}
                      disabled
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Payment Methods Settings */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-purple-500" />
                    Accepted Payment Methods for Booth Rent
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-gray-600">
                    Configure how your booth renters can pay their rent. These options will be included in payment request emails.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Stripe/Card Payments */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-indigo-100 rounded flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-indigo-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Card Payments (Stripe)</Label>
                            <p className="text-xs text-gray-500">Accept debit/credit cards</p>
                          </div>
                        </div>
                        <Switch
                          checked={boothSettings.accept_stripe_payments}
                          onCheckedChange={(v) => setBoothSettings(prev => ({...prev, accept_stripe_payments: v}))}
                        />
                      </div>
                      {boothSettings.accept_stripe_payments && (
                        <div className="bg-green-50 border border-green-200 rounded p-3">
                          <p className="text-green-700 text-xs flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Uses your existing Stripe Connect account
                          </p>
                        </div>
                      )}
                    </div>

                    {/* CashApp */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                            <DollarSign className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <Label className="font-medium">CashApp</Label>
                            <p className="text-xs text-gray-500">Accept CashApp payments</p>
                          </div>
                        </div>
                        <Switch
                          checked={boothSettings.accept_cashapp}
                          onCheckedChange={(v) => setBoothSettings(prev => ({...prev, accept_cashapp: v}))}
                        />
                      </div>
                      {boothSettings.accept_cashapp && (
                        <Input
                          placeholder="$YourCashTag"
                          value={boothSettings.cashapp_tag}
                          onChange={(e) => setBoothSettings(prev => ({...prev, cashapp_tag: e.target.value}))}
                          className="text-sm"
                        />
                      )}
                    </div>

                    {/* Zelle */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center">
                            <DollarSign className="w-4 h-4 text-purple-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Zelle</Label>
                            <p className="text-xs text-gray-500">Accept Zelle payments</p>
                          </div>
                        </div>
                        <Switch
                          checked={boothSettings.accept_zelle}
                          onCheckedChange={(v) => setBoothSettings(prev => ({...prev, accept_zelle: v}))}
                        />
                      </div>
                      {boothSettings.accept_zelle && (
                        <div className="space-y-2">
                          <Input
                            placeholder="zelle@email.com"
                            value={boothSettings.zelle_email}
                            onChange={(e) => setBoothSettings(prev => ({...prev, zelle_email: e.target.value}))}
                            className="text-sm"
                          />
                          <Input
                            placeholder="Or phone number"
                            value={boothSettings.zelle_phone}
                            onChange={(e) => setBoothSettings(prev => ({...prev, zelle_phone: e.target.value}))}
                            className="text-sm"
                          />
                        </div>
                      )}
                    </div>

                    {/* PayPal */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                            <DollarSign className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <Label className="font-medium">PayPal</Label>
                            <p className="text-xs text-gray-500">Accept PayPal payments</p>
                          </div>
                        </div>
                        <Switch
                          checked={boothSettings.accept_paypal}
                          onCheckedChange={(v) => setBoothSettings(prev => ({...prev, accept_paypal: v}))}
                        />
                      </div>
                      {boothSettings.accept_paypal && (
                        <Input
                          placeholder="paypal@email.com"
                          value={boothSettings.paypal_email}
                          onChange={(e) => setBoothSettings(prev => ({...prev, paypal_email: e.target.value}))}
                          className="text-sm"
                        />
                      )}
                    </div>

                    {/* Cash Payment */}
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-emerald-100 rounded flex items-center justify-center">
                            <DollarSign className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <Label className="font-medium">Cash Payment</Label>
                            <p className="text-xs text-gray-500">Accept cash at salon</p>
                          </div>
                        </div>
                        <Switch
                          checked={boothSettings.accept_cash}
                          onCheckedChange={(v) => setBoothSettings(prev => ({...prev, accept_cash: v}))}
                        />
                      </div>
                      {boothSettings.accept_cash && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                          <p className="text-emerald-700 text-xs">
                            💵 Renters can pay cash in person. You&apos;ll mark it as paid when received.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {(boothSettings.accept_cashapp || boothSettings.accept_zelle || boothSettings.accept_paypal) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-800 text-sm flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span>
                          <strong>Digital Payment Methods:</strong> When renters pay via CashApp, Zelle, or PayPal, 
                          they will be prompted to upload a screenshot of their payment confirmation. 
                          You&apos;ll need to verify the receipt and manually mark the payment as paid.
                        </span>
                      </p>
                    </div>
                  )}

                  {boothSettings.accept_cash && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <p className="text-emerald-800 text-sm flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span>
                          <strong>Cash Payments:</strong> When renters pay cash, the payment will show as &quot;Pending&quot; 
                          until you check the &quot;Payment Received&quot; box in Booth Management.
                        </span>
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Export Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-purple-500" />
                    Export Payment History
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Download your complete payment history for accounting or record keeping.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => exportPaymentHistory('csv')}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Export CSV
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => exportPaymentHistory('json')}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Export JSON
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Monthly Auto Payment Links */}
              <Card className="md:col-span-2 border-2 border-purple-200 bg-purple-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-500" />
                    Monthly Auto Payment Links
                    <Badge className="ml-2 bg-purple-500 text-white">NEW</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-gray-600">
                    Automatically generate and send Stripe payment links to all your renters on a specific day each month. 
                    No more manually creating payments!
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Enable Auto Links */}
                    <div className="space-y-3 p-4 bg-white rounded-lg border">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-base font-medium">Enable Monthly Auto-Generation</Label>
                          <p className="text-sm text-gray-500">Automatically create payment links on your billing day</p>
                        </div>
                        <Switch
                          checked={boothSettings.auto_monthly_links_enabled}
                          onCheckedChange={(v) => setBoothSettings(prev => ({...prev, auto_monthly_links_enabled: v}))}
                        />
                      </div>
                    </div>

                    {/* Billing Day */}
                    <div className="space-y-3 p-4 bg-white rounded-lg border">
                      <Label className="text-base font-medium">Billing Day of Month</Label>
                      <p className="text-sm text-gray-500 mb-2">Day to generate and send payment links</p>
                      <Select
                        value={String(boothSettings.billing_day || 1)}
                        onValueChange={(v) => setBoothSettings(prev => ({...prev, billing_day: parseInt(v)}))}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select billing day" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 5, 10, 15, 20, 25, 28].map(day => (
                            <SelectItem key={day} value={String(day)}>
                              {day === 1 ? "1st" : day === 2 ? "2nd" : day === 3 ? "3rd" : `${day}th`} of each month
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {boothSettings.auto_monthly_links_enabled && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800 text-sm flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span>
                          <strong>How it works:</strong> On the <strong>{boothSettings.billing_day || 1}{boothSettings.billing_day === 1 ? 'st' : boothSettings.billing_day === 2 ? 'nd' : boothSettings.billing_day === 3 ? 'rd' : 'th'}</strong> of 
                          each month, the system will automatically create Stripe payment links for all active renters 
                          and email them the payment request. Payments are tracked in your Booth Management tab.
                        </span>
                      </p>
                    </div>
                  )}

                  {/* Manual Generate Button */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-medium">Generate Payment Links Now</Label>
                        <p className="text-sm text-gray-500">Manually create this month's payment links for all active renters</p>
                      </div>
                      <Button
                        className="bg-gradient-to-r from-purple-500 to-indigo-600"
                        onClick={async () => {
                          setGeneratingMonthlyLinks(true);
                          try {
                            const res = await axios.post(`${API}/booth/generate-monthly-payment-links?force=true`, {}, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            if (res.data.generated > 0) {
                              toast.success(`Generated ${res.data.generated} payment links and sent emails!`);
                            } else {
                              toast.info(res.data.message || "No new payment links to generate");
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Failed to generate payment links");
                          } finally {
                            setGeneratingMonthlyLinks(false);
                          }
                        }}
                        disabled={generatingMonthlyLinks}
                      >
                        {generatingMonthlyLinks ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Generate & Send Links
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                className="bg-gradient-to-r from-purple-500 to-indigo-600 px-8"
                onClick={saveBoothSettings}
                disabled={savingSettings}
              >
                {savingSettings ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Approve Application Modal */}
      <Dialog open={showApproveModal} onOpenChange={setShowApproveModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Application</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="font-medium">{selectedApp.applicant_name}</p>
                <p className="text-sm text-gray-500">{selectedApp.applicant_email}</p>
                {selectedApp.promo_details && (
                  <Badge className="mt-2 bg-purple-100 text-purple-700">
                    Promo Applied: {selectedApp.promo_details.description || selectedApp.promo_code}
                  </Badge>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Booth/Suite Number</Label>
                  <Input
                    value={approveForm.booth_number}
                    onChange={(e) => setApproveForm({...approveForm, booth_number: e.target.value})}
                    placeholder="1A"
                  />
                </div>
                <div>
                  <Label>Monthly Rent ($)</Label>
                  <Input
                    type="number"
                    value={approveForm.rent_amount}
                    onChange={(e) => setApproveForm({...approveForm, rent_amount: e.target.value})}
                    placeholder="800"
                  />
                </div>
              </div>
              <div>
                <Label>Payment Due Day (1-28)</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={approveForm.payment_due_day}
                  onChange={(e) => setApproveForm({...approveForm, payment_due_day: parseInt(e.target.value) || 1})}
                />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={approveForm.notes}
                  onChange={(e) => setApproveForm({...approveForm, notes: e.target.value})}
                  placeholder="Any notes about this renter..."
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveModal(false)}>Cancel</Button>
            <Button onClick={approveApplication} className="bg-green-500 hover:bg-green-600">
              <Check className="w-4 h-4 mr-1" />
              Approve & Add Renter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade to Elite Modal */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md bg-gradient-to-br from-stone-900 to-stone-800 border-purple-500/30 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Crown className="w-6 h-6 text-purple-400" />
              Upgrade to Elite
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="text-center">
              <Lock className="w-16 h-16 text-purple-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {upgradeFeature ? `"${upgradeFeature}" is an Elite Feature` : 'Salon Owner Portal is Elite Only'}
              </h3>
              <p className="text-stone-400 text-sm">
                The Salon Owner Dashboard with booth management, rent collection, and marketing tools is exclusively available for Elite members.
              </p>
            </div>

            <div className="bg-stone-800/50 rounded-lg p-4 space-y-2">
              <p className="text-sm text-purple-400 font-medium mb-3">Elite Features Include:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Booth Rental Management</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Automated Rent Collection via Stripe</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Marketing Cards & QR Codes</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Promo Codes & Application Forms</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Email Rent Reminders</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Payment History Export</span>
                </li>
              </ul>
            </div>

            <div className="text-center py-2">
              <span className="text-4xl font-bold text-white">$29.99</span>
              <span className="text-stone-400">/month</span>
              <p className="text-xs text-green-400 mt-1">First 3 months at intro pricing!</p>
            </div>

            <Button
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/membership?feature=salon_owner&tier=elite&return=/owner-dashboard');
              }}
              className="w-full h-12 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-bold text-lg"
              data-testid="upgrade-elite-modal-btn"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Elite Now
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stripe Info Card for non-elite showing at bottom */}
      {!isElite && (
        <div className="fixed bottom-4 right-4 max-w-md z-40">
          <Card className="bg-gradient-to-br from-stone-900 to-stone-800 border-purple-500/30 shadow-2xl">
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-lg font-bold text-white">Unlock Salon Owner Tools</p>
                  <p className="text-sm text-gray-400 mt-1">
                    Get full access to booth management, rent collection, and marketing tools.
                  </p>
                  <div className="flex flex-col gap-2 mt-4">
                    <Button
                      className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-bold"
                      onClick={() => navigate('/membership?tier=elite&trial=7')}
                      data-testid="start-trial-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      Try Elite FREE for 7 Days
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                      onClick={() => navigate('/membership?tier=elite')}
                    >
                      View All Plans
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-3 text-center">
                    No credit card required for trial • Cancel anytime
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trial Expired Modal */}
      <TrialExpiredModal
        isOpen={showTrialExpiredModal}
        onClose={() => setShowTrialExpiredModal(false)}
        previousTier={membershipTier || "elite"}
        onContinueFree={handleTrialExpiredContinueFree}
      />

      {/* Soldier Recruitment Playbook */}
      <SoldierRecruitmentPlaybook 
        isOpen={showPlaybook}
        onClose={() => setShowPlaybook(false)}
        referralCode={user?.soldier_referral_code || 'YOUR-CODE'}
        discountValue="14 days FREE + $10 off for 3 months"
      />
    </div>
  );
};

export default OwnerDashboard;
